
import { useState, useCallback, useEffect } from 'react';
import { useWebSocket } from '../hooks/useWebSocket';

function TagInput({ label, placeholder, values, onChange, max=5 }) {
  const [input, setInput] = useState('');
  const add = () => {
    const v = input.trim();
    if (v && values.length < max && !values.includes(v)) {
      onChange([...values, v]);
    }
    setInput('');
  };
  const remove = (i) => onChange(values.filter((_,j)=>j!==i));
  return (
    <div style={{display:'flex',flexDirection:'column',gap:5}}>
      <div style={{fontSize:'0.58rem',letterSpacing:'0.15em',color:'#4a6a9e',textTransform:'uppercase'}}>{label}</div>
      <div style={{display:'flex',gap:6}}>
        <input value={input} onChange={e=>setInput(e.target.value)}
          onKeyDown={e=>e.key==='Enter'&&add()}
          placeholder={placeholder}
          style={{flex:1,padding:'7px 10px',borderRadius:7,background:'#0a1628',border:'1px solid #1e3a6e',
            color:'#e8edf8',fontFamily:'Inter,sans-serif',fontSize:'0.8rem',outline:'none'}}/>
        <button onClick={add} disabled={values.length>=max}
          style={{padding:'7px 12px',borderRadius:7,background:'#0369a1',border:'none',color:'#fff',
            cursor:'pointer',fontSize:'0.78rem',fontFamily:'Inter,sans-serif'}}>Add</button>
      </div>
      {values.length>0&&<div style={{display:'flex',flexWrap:'wrap',gap:5}}>
        {values.map((v,i)=>(
          <span key={i} style={{fontSize:'0.7rem',color:'#38bdf8',background:'rgba(56,189,248,0.1)',
            border:'1px solid #0369a1',borderRadius:5,padding:'2px 8px',cursor:'pointer'}}
            onClick={()=>remove(i)}>{v} ✕</span>
        ))}
      </div>}
      <div style={{fontSize:'0.55rem',color:'#3a5a6e'}}>Press Enter or Add · Click a name to remove · max {max}</div>
    </div>
  );
}

function LeagueTable({ table, myName }) {
  if (!table || table.length === 0) return null;
  return (
    <div className="pq-scoreboard" style={{width:'100%'}}>
      <div className="pq-score-hdr">Live Standings</div>
      {table.map((row,i)=>(
        <div key={row.name} className="pq-score-row"
          style={{background: row.name===myName ? 'rgba(56,189,248,0.07)' : 'transparent',
            flexDirection:'column',alignItems:'flex-start',gap:2}}>
          <div style={{display:'flex',width:'100%',alignItems:'center',gap:8}}>
            <span className="pq-score-pos">{i===0?'🥇':i===1?'🥈':i===2?'🥉':('#'+(i+1))}</span>
            <span className="pq-score-name" style={{flex:1,color:row.name===myName?'#38bdf8':'#e8edf8'}}>
              {row.name}{row.name===myName?' (you)':''}
            </span>
            <span className="pq-score-pts">{row.pts} pts</span>
          </div>
          {row.name===myName&&row.breakdown?.length>0&&(
            <div style={{paddingLeft:28,display:'flex',flexWrap:'wrap',gap:3,marginTop:2}}>
              {row.breakdown.map((b,j)=>(
                <span key={j} style={{fontSize:'0.58rem',color:'#4ade80',background:'rgba(34,197,94,0.1)',
                  borderRadius:4,padding:'1px 6px'}}>+{b.pts} {b.desc}</span>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function LiveScore({ liveData }) {
  if (!liveData) return null;
  const goals = liveData.goals || [];
  const cards = liveData.bookings || [];
  return (
    <div style={{background:'#0a1628',border:'1px solid #22c55e',borderRadius:12,padding:'14px 16px',textAlign:'center'}}>
      <div style={{fontSize:'0.55rem',color:'#22c55e',letterSpacing:'0.15em',textTransform:'uppercase',marginBottom:8,
        animation:'pulse 1.5s ease infinite'}}>● LIVE</div>
      <div style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:'3rem',color:'#f5c518',lineHeight:1}}>
        {liveData.homeScore??0} – {liveData.awayScore??0}
      </div>
      {liveData.minute&&<div style={{fontSize:'0.7rem',color:'#22c55e',marginTop:2}}>⏱ {liveData.minute}'</div>}
      {goals.map((g,i)=>(
        <div key={i} style={{fontSize:'0.67rem',color:'#e8edf8',marginTop:3}}>
          ⚽ {g.minute}' {g.scorer}{g.type==='OWN_GOAL'?' (OG)':''} ({g.team})
        </div>
      ))}
      {cards.map((c,i)=>(
        <div key={i} style={{fontSize:'0.65rem',color:'#e8edf8'}}>
          {c.card==='YELLOW_CARD'?'🟨':'🟥'} {c.minute}' {c.player}
        </div>
      ))}
    </div>
  );
}

export default function PredictorPlayer({ onBack }) {
  const [phase,setPhase]       = useState('join');   // join|predicting|submitted|live|finished
  const [codeInput,setCodeInput]= useState('');
  const [nameInput,setNameInput]= useState('');
  const [error,setError]        = useState('');
  const [myName,setMyName]      = useState('');
  const [match,setMatch]        = useState(null);
  const [sessionCode,setSessionCode]=useState('');
  const [state,setState]        = useState(null);
  const [confirmed,setConfirmed]= useState(false);

  // Prediction fields
  const [homeGoals,setHomeGoals]   = useState('');
  const [awayGoals,setAwayGoals]   = useState('');
  const [scorers,setScorers]       = useState([]);
  const [yellowCards,setYellowCards]=useState([]);
  const [redCards,setRedCards]     = useState([]);
  const [corners,setCorners]       = useState('');

  const onMsg = useCallback((msg) => {
    switch(msg.type) {
      case 'PREDICTOR_JOINED':
      case 'PREDICTOR_REJOINED':
        setMyName(msg.name); setMatch(msg.match); setSessionCode(msg.code);
        setPhase('predicting'); break;
      case 'PREDICTOR_CONFIRMED':
        setConfirmed(true); setPhase('submitted'); break;
      case 'PREDICTOR_STATE':
        setState(msg);
        // Restore own prediction if rejoining
        if (msg.predictions?.[myName||nameInput.trim()]) {
          const p = msg.predictions[myName||nameInput.trim()];
          setHomeGoals(String(p.homeGoals??''));
          setAwayGoals(String(p.awayGoals??''));
          setScorers(p.scorers||[]);
          setYellowCards(p.yellowCards||[]);
          setRedCards(p.redCards||[]);
          setCorners(p.corners!==undefined?String(p.corners):'');
          setConfirmed(true);
        }
        if (msg.status==='finished') setPhase('finished');
        else if (msg.status==='live') { if(confirmed||phase==='submitted') setPhase('live'); }
        break;
      case 'ERROR':
        setError(msg.message); break;
    }
  }, [myName, nameInput, confirmed, phase]);

  const { send } = useWebSocket(onMsg);

  const join = useCallback(() => {
    const code = codeInput.trim().toUpperCase();
    const name = nameInput.trim();
    if (code.length !== 4) { setError('Enter the 4-letter code'); return; }
    if (!name) { setError('Enter your name'); return; }
    setError('');
    send('PREDICTOR_JOIN', { code, name });
  }, [codeInput, nameInput, send]);

  const submit = useCallback(() => {
    if (homeGoals===''||awayGoals==='') { setError('Enter a score prediction'); return; }
    send('PREDICTOR_SUBMIT', {
      result: parseInt(homeGoals) > parseInt(awayGoals) ? 'H' : parseInt(awayGoals) > parseInt(homeGoals) ? 'A' : 'D',
      homeGoals: parseInt(homeGoals),
      awayGoals: parseInt(awayGoals),
      scorers, yellowCards, redCards,
      corners: corners !== '' ? parseInt(corners) : undefined
    });
  }, [homeGoals, awayGoals, scorers, yellowCards, redCards, corners, send]);

  const myPrediction = state?.predictions?.[myName];
  const table        = state?.table || [];
  const liveData     = state?.liveData;

  /* ── JOIN ── */
  if (phase==='join') return (
    <div className="layer" style={{display:'flex',flexDirection:'column',minHeight:'100vh'}}>
      <div className="g-hdr"><button className="g-back" onClick={onBack}>← Menu</button>
        <div className="g-title" style={{color:'#38bdf8'}}>JOIN PREDICTOR</div>
      </div>
      <div className="join-wrap"><div className="join-card">
        <div className="join-title" style={{color:'#38bdf8'}}>🎯 JOIN</div>
        <p style={{fontSize:'0.74rem',color:'#8ba3cc'}}>Enter the session code from the host screen</p>
        <input className="join-input" value={codeInput} placeholder="CODE"
          onChange={e=>setCodeInput(e.target.value.toUpperCase().slice(0,4))} maxLength={4}/>
        <input className="join-name-input" value={nameInput} placeholder="Your name"
          onChange={e=>setNameInput(e.target.value.slice(0,16))}/>
        {error&&<div className="join-error">{error}</div>}
        <button className="btn-blue" onClick={join}>JOIN SESSION</button>
        <p style={{fontSize:'0.64rem',color:'#3a5a6e',lineHeight:1.5}}>
          You can leave and rejoin at any time using this code.<br/>Your predictions will be saved.
        </p>
      </div></div>
    </div>
  );

  /* ── PREDICTING ── */
  if (phase==='predicting'||phase==='submitted') return (
    <div className="layer" style={{display:'flex',flexDirection:'column',minHeight:'100vh'}}>
      <div className="g-hdr">
        <button className="g-back" onClick={onBack}>← Leave</button>
        <div className="g-title" style={{color:'#38bdf8'}}>PREDICT</div>
        <div className="g-pills"><div className="g-pill b"><span className="g-plbl">Code</span><span className="g-pval">{sessionCode}</span></div></div>
      </div>
      <div className="game-body" style={{maxWidth:480}}>
        {/* Match */}
        {match&&<div style={{background:'#0f1e38',border:'1px solid #1e3a6e',borderRadius:12,padding:'14px 16px',textAlign:'center'}}>
          <div style={{fontSize:'0.58rem',color:'#8ba3cc',textTransform:'uppercase',letterSpacing:'0.15em'}}>{match.competition}</div>
          <div style={{display:'flex',alignItems:'center',justifyContent:'center',gap:14,marginTop:6}}>
            <span style={{fontSize:'0.96rem',fontWeight:700,color:'#e8edf8'}}>{match.homeShort}</span>
            <span style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:'1.4rem',color:'#3a5a8e'}}>VS</span>
            <span style={{fontSize:'0.96rem',fontWeight:700,color:'#e8edf8'}}>{match.awayShort}</span>
          </div>
          <div style={{fontSize:'0.62rem',color:'#4a6a9e',marginTop:4}}>
            {new Date(match.utcDate).toLocaleString([],{weekday:'short',hour:'2-digit',minute:'2-digit'})}
          </div>
        </div>}

        {confirmed&&<div style={{background:'rgba(34,197,94,0.09)',border:'1px solid #22c55e',borderRadius:10,
          padding:'10px 14px',textAlign:'center',fontSize:'0.78rem',color:'#4ade80',fontWeight:600}}>
          ✓ Prediction saved · You can leave and rejoin using code <strong>{sessionCode}</strong>
        </div>}

        {/* Score */}
        <div style={{display:'flex',flexDirection:'column',gap:6}}>
          <div style={{fontSize:'0.58rem',letterSpacing:'0.15em',color:'#4a6a9e',textTransform:'uppercase'}}>Predicted Score *</div>
          <div className="score-inputs" style={{justifyContent:'flex-start',gap:10}}>
            <div style={{display:'flex',flexDirection:'column',alignItems:'center',gap:3}}>
              <span style={{fontSize:'0.6rem',color:'#8ba3cc'}}>{match?.homeShort}</span>
              <input className="score-input" type="number" min="0" max="20" value={homeGoals}
                onChange={e=>setHomeGoals(e.target.value)} placeholder="0" style={{width:56,height:56}}/>
            </div>
            <span className="score-dash" style={{paddingTop:18}}>-</span>
            <div style={{display:'flex',flexDirection:'column',alignItems:'center',gap:3}}>
              <span style={{fontSize:'0.6rem',color:'#8ba3cc'}}>{match?.awayShort}</span>
              <input className="score-input" type="number" min="0" max="20" value={awayGoals}
                onChange={e=>setAwayGoals(e.target.value)} placeholder="0" style={{width:56,height:56}}/>
            </div>
          </div>
        </div>

        <TagInput label="Goalscorers (2 pts each)" placeholder="Player surname..." values={scorers} onChange={setScorers} max={6}/>
        <TagInput label="Yellow cards (1 pt each)" placeholder="Player surname..." values={yellowCards} onChange={setYellowCards} max={5}/>
        <TagInput label="Red cards (2 pts each)" placeholder="Player surname..." values={redCards} onChange={setRedCards} max={3}/>

        <div style={{display:'flex',flexDirection:'column',gap:5}}>
          <div style={{fontSize:'0.58rem',letterSpacing:'0.15em',color:'#4a6a9e',textTransform:'uppercase'}}>Total corners (±2 = 2 pts)</div>
          <input type="number" min="0" max="30" value={corners} onChange={e=>setCorners(e.target.value)}
            placeholder="e.g. 9"
            style={{width:90,padding:'8px 12px',borderRadius:8,background:'#0a1628',border:'1px solid #1e3a6e',
              color:'#f5c518',fontFamily:"'Bebas Neue',sans-serif",fontSize:'1.4rem',outline:'none',textAlign:'center'}}/>
        </div>

        {error&&<div className="join-error">{error}</div>}

        <div style={{fontSize:'0.6rem',color:'#3a5a6e',lineHeight:1.6,borderTop:'1px solid #1e3a6e',paddingTop:10}}>
          Scoring: <span style={{color:'#e8edf8'}}>Result 3pts · Exact score +5pts · Each scorer 2pts · Yellow 1pt · Red 2pts · Corners (±2) 2pts</span>
        </div>

        <button className="btn-blue" onClick={submit}>{confirmed?'Update Prediction':'Submit Prediction'}</button>

        {/* Live table preview */}
        {table.length>0&&<LeagueTable table={table} myName={myName}/>}
      </div>
    </div>
  );

  /* ── LIVE ── */
  if (phase==='live') return (
    <div className="layer" style={{display:'flex',flexDirection:'column',minHeight:'100vh'}}>
      <div className="g-hdr">
        <button className="g-back" onClick={onBack}>← Leave</button>
        <div className="g-title" style={{color:'#22c55e'}}>● LIVE</div>
        <div className="g-pills"><div className="g-pill b"><span className="g-plbl">Code</span><span className="g-pval">{sessionCode}</span></div></div>
      </div>
      <div className="game-body" style={{maxWidth:480}}>
        <LiveScore liveData={liveData}/>
        {myPrediction&&<div style={{background:'#0f1e38',border:'1px solid #1e3a6e',borderRadius:10,padding:'12px 14px'}}>
          <div style={{fontSize:'0.55rem',letterSpacing:'0.15em',color:'#4a6a9e',textTransform:'uppercase',marginBottom:5}}>Your Prediction</div>
          <div style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:'1.8rem',color:'#f5c518'}}>
            {myPrediction.homeGoals} – {myPrediction.awayGoals}
          </div>
          {myPrediction.scorers?.length>0&&<div style={{fontSize:'0.67rem',color:'#8ba3cc'}}>⚽ {myPrediction.scorers.join(', ')}</div>}
        </div>}
        <LeagueTable table={table} myName={myName}/>
        <p style={{fontSize:'0.64rem',color:'#4a6a9e',textAlign:'center'}}>Updating every 60s · Rejoin at any time with code <strong style={{color:'#38bdf8'}}>{sessionCode}</strong></p>
      </div>
    </div>
  );

  /* ── FINISHED ── */
  if (phase==='finished') return (
    <div className="layer" style={{display:'flex',flexDirection:'column',minHeight:'100vh'}}>
      <div className="g-hdr">
        <button className="g-back" onClick={onBack}>← Menu</button>
        <div className="g-title" style={{color:'#f5c518'}}>FULL TIME</div>
        <div className="g-pills"><div className="g-pill b"><span className="g-plbl">Code</span><span className="g-pval">{sessionCode}</span></div></div>
      </div>
      <div className="game-body" style={{maxWidth:480}}>
        {liveData&&<div style={{background:'#0a1628',border:'1px solid #1e3a6e',borderRadius:12,padding:'14px 16px',textAlign:'center'}}>
          <div style={{fontSize:'0.55rem',letterSpacing:'0.15em',color:'#f5c518',textTransform:'uppercase'}}>Full Time</div>
          <div style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:'3rem',color:'#f5c518',lineHeight:1}}>
            {liveData.homeScore??0} – {liveData.awayScore??0}
          </div>
          <div style={{fontSize:'0.72rem',color:'#8ba3cc'}}>{liveData.homeTeam} vs {liveData.awayTeam}</div>
        </div>}
        <LeagueTable table={table} myName={myName}/>
        <p style={{fontSize:'0.64rem',color:'#4a6a9e',textAlign:'center'}}>
          Session open for 24h · Share code <strong style={{color:'#38bdf8'}}>{sessionCode}</strong> so others can check their final score
        </p>
        <button className="btn-gold" onClick={onBack}>Back to Menu</button>
      </div>
    </div>
  );

  return null;
}
